import { orderAutomationFields } from '../../../packages/shared/automation';
import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Select } from '../components/forms';
/**
 * AUTOMATION RULE EDITOR
 *
 * Visual builder for creating and editing automation rules.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Spinner,
  Checkbox,
  Box,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface Condition {
  field: string;
  operator: string;
  value: string | number | boolean;
}

interface Action {
  type: string;
  value: string;
  params?: Record<string, unknown>;
}

interface AutomationRule {
  id?: number;
  name: string;
  trigger_event: string;
  conditions: Condition[];
  actions: Action[];
  priority: number;
  enabled: boolean;
}

const triggerOptions = [
  { label: 'Order Created', value: 'order_created' },
  { label: 'Order Allocated', value: 'order_allocated' },
  { label: 'Order Picked', value: 'order_picked' },
  { label: 'Order Packed', value: 'order_packed' },
  { label: 'Order Dispatched', value: 'order_dispatched' },
  { label: 'Stock Low (preview only)', value: 'stock_low' },
  { label: 'Return Requested (preview only)', value: 'return_requested' },
];

const conditionFields = orderAutomationFields.map(value => ({ value, label: value.replace(/_/g, ' ') }));

const conditionOperators = [
  { label: 'equals', value: 'equals' },
  { label: 'not equals', value: 'not_equals' },
  { label: 'contains', value: 'contains' },
  { label: 'not contains', value: 'not_contains' },
  { label: 'greater than', value: 'greater_than' },
  { label: 'less than', value: 'less_than' },
  { label: 'is empty', value: 'is_empty' },
  { label: 'is not empty', value: 'is_not_empty' },
];

const actionTypes = [
  { label: 'Add Tag', value: 'set_tag' },
  { label: 'Remove Tag', value: 'remove_tag' },
  { label: 'Add Note', value: 'add_note' },
  { label: 'Set Status (preview only)', value: 'set_status' },
  { label: 'Set Priority', value: 'set_priority' },
  { label: 'Send Notification (preview only)', value: 'notify' },
  { label: 'Call Webhook (preview only)', value: 'webhook' },
];

export default function AutomationRuleEditor() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [saving, setSaving] = useState(false);

  const isNew = !ruleId || ruleId === 'new';

  const [name, setName] = useState('');
  const [triggerEvent, setTriggerEvent] = useState('order_created');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [priority, setPriority] = useState('0');
  const [enabled, setEnabled] = useState(true);

  const loadRule = useCallback(async () => {
    if (isNew) return;

    const data = await fetchApi<{ data: AutomationRule }>(`/automation/rules/${ruleId}`);
    if (data?.data) {
      setName(data.data.name);
      setTriggerEvent(data.data.trigger_event);
      setConditions(data.data.conditions || []);
      setActions((data.data.actions || []).map(action => {
        const key = ({ set_tag: 'tag', remove_tag: 'tag', add_note: 'note', set_priority: 'priority' } as Record<string, string>)[action.type];
        return { ...action, value: typeof action.value === 'string' ? action.value : typeof action.params?.[key] === 'string' ? String(action.params[key]) : '' };
      }));
      setPriority(String(data.data.priority));
      setEnabled(Boolean(data.data.enabled));
    }
  }, [fetchApi, ruleId, isNew]);

  useEffect(() => {
    loadRule();
  }, [loadRule]);

  const addCondition = () => {
    setConditions([...conditions, { field: 'order_total', operator: 'equals', value: '' }]);
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updates };
    setConditions(newConditions);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addAction = () => {
    setActions([...actions, { type: 'set_tag', value: '' }]);
  };

  const updateAction = (index: number, updates: Partial<Action>) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], ...updates };
    setActions(newActions);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (actions.length === 0) return;

    setSaving(true);

    const payload = {
      name,
      trigger_event: triggerEvent,
      conditions,
      actions,
      priority: parseInt(priority, 10),
      enabled,
    };

    const endpoint = isNew ? '/automation/rules' : `/automation/rules/${ruleId}`;
    const method = isNew ? 'POST' : 'PUT';

    const result = await fetchApi(endpoint, {
      method,
      body: JSON.stringify(payload),
    });

    setSaving(false);

    if (result) {
      navigate('/automation');
    }
  };

  if (loading && !isNew) {
    return (
      <Page title="Edit Automation Rule">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading rule...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={isNew ? 'Create Automation Rule' : 'Edit Automation Rule'}
      backAction={{ content: 'Automation Rules', onAction: () => navigate('/automation') }}
      primaryActionPosition="footer"
      primaryAction={{
        content: 'Save Rule',
        onAction: handleSave,
        loading: saving,
        disabled: !name.trim() || actions.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Basic Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Rule Details</Text>

              <TextField
                label="Rule Name"
                requiredIndicator value={name}
                onChange={setName}
                autoComplete="off"
                placeholder="e.g., Tag fragile items"
              />

              <Select
                label="Trigger Event"
                options={triggerOptions.some(option => option.value === triggerEvent) ? triggerOptions : [...triggerOptions, { value: triggerEvent, label: `${triggerEvent} (saved trigger, preview only)` }]}
                requiredIndicator value={triggerEvent}
                onChange={setTriggerEvent}
                helpText="After saving, use Review execution to activate supported order rules. Editing a rule pauses execution."
              />

              <InlineStack gap="400">
                <div style={{ width: '100px' }}>
                  <TextField
                    label="Priority"
                    type="number"
                    value={priority}
                    onChange={setPriority}
                    autoComplete="off"
                    helpText="Higher = runs first"
                  />
                </div>
                <div style={{ paddingTop: '24px' }}>
                  <Checkbox
                    label="Saved definition is enabled"
                    checked={enabled}
                    onChange={setEnabled}
                  />
                </div>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Conditions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Conditions</Text>
                  <Text as="p" tone="subdued">
                    All conditions must be met for actions to execute (optional)
                  </Text>
                </BlockStack>
                <Button icon={PlusIcon} onClick={addCondition}>Add Condition</Button>
              </InlineStack>

              {conditions.length === 0 ? (
                <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                  <Text as="p" tone="subdued" alignment="center">
                    No conditions set. Rule will trigger on every event.
                  </Text>
                </Box>
              ) : (
                conditions.map((condition, index) => (
                  <Box
                    key={index}
                    padding="400"
                    background="bg-surface-secondary"
                    borderRadius="200"
                  >
                    <InlineStack gap="300" blockAlign="end" wrap>
                      <div style={{ width: '180px' }}>
                        <Select
                          label="Field"
                          options={[...conditionFields, ...conditions.filter(row => !orderAutomationFields.some(field => field === row.field)).map(row => ({ value: row.field, label: `${row.field} (saved field)` }))]}
                          requiredIndicator value={condition.field}
                          onChange={(val) => updateCondition(index, { field: val })}
                        />
                      </div>
                      <div style={{ width: '150px' }}>
                        <Select
                          label="Operator"
                          options={conditionOperators}
                          requiredIndicator value={condition.operator}
                          onChange={(val) => updateCondition(index, { operator: val })}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <TextField
                          label="Value"
                          requiredIndicator={!['is_empty','is_not_empty'].includes(condition.operator)} value={String(condition.value ?? '')}
                          onChange={(val) => updateCondition(index, { value: val })}
                          autoComplete="off"
                          disabled={['is_empty', 'is_not_empty'].includes(condition.operator)}
                        />
                      </div>
                      <Button
                        tone="critical"
                        onClick={() => removeCondition(index)}
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  </Box>
                ))
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Actions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Actions</Text>
                  <Text as="p" tone="subdued">
                    Actions to perform when the rule triggers
                  </Text>
                </BlockStack>
                <Button icon={PlusIcon} onClick={addAction}>Add Action</Button>
              </InlineStack>

              {actions.length === 0 ? (
                <Banner tone="warning">
                  Add at least one action for this rule to do something.
                </Banner>
              ) : (
                actions.map((action, index) => (
                  <Box
                    key={index}
                    padding="400"
                    background="bg-surface-secondary"
                    borderRadius="200"
                  >
                    <InlineStack gap="300" blockAlign="end" wrap>
                      <div style={{ width: '200px' }}>
                        <Select
                          label="Action Type"
                          options={actionTypes.some(option => option.value === action.type) ? actionTypes : [...actionTypes, { value: action.type, label: `${action.type} (saved action, preview only)` }]}
                          requiredIndicator value={action.type}
                          onChange={(val) => updateAction(index, { type: val })}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <TextField
                          label={getActionValueLabel(action.type)}
                          requiredIndicator value={action.value}
                          onChange={(val) => updateAction(index, { value: val })}
                          autoComplete="off"
                          placeholder={getActionPlaceholder(action.type)}
                        />
                      </div>
                      <Button
                        tone="critical"
                        onClick={() => removeAction(index)}
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  </Box>
                ))
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function getActionValueLabel(type: string): string {
  const labels: Record<string, string> = {
    set_tag: 'Tag Name',
    remove_tag: 'Tag Name',
    add_note: 'Note',
    set_status: 'Status',
    set_priority: 'Priority (normal/high/urgent)',
    notify: 'Email Address',
    webhook: 'Webhook URL',
  };
  return labels[type] || 'Value';
}

function getActionPlaceholder(type: string): string {
  const placeholders: Record<string, string> = {
    set_tag: 'e.g., fragile',
    remove_tag: 'e.g., fragile',
    add_note: 'e.g., Check packaging before dispatch',
    set_status: 'e.g., requires_review',
    set_priority: 'normal, high, or urgent',
    notify: 'email@example.com',
    webhook: 'https://...',
  };
  return placeholders[type] || '';
}
